// Delivery pipeline for a stored cancellation report. Runs outside React so the same code serves
// the Send tap, the receipt's Retry, the `online` event and the app-start flush — and so a
// report keeps sending after the agent quick-hides the panel.
//
// Order is the point (STORY-71 v2, Phase 4):
//   1. core report — small and urgent; the party alert must never wait on media or VESR
//   2. evidence — each file independently: sign → chunks through the api.core relay → complete → register
//   3. neutral Situation Room incident (no notes, no media) → link it on the core row
// Every step persists its outcome before the next begins, so a crash or a dead battery resumes
// where it stopped, with the same referenceId.
import { reasonCategory, reasonLabel } from '../components/pages/pu-cancellation/reasons';
import { getUserData } from '../constants/global';
import type { ICreateIncidentRequest, IIncident, IVesrOutcome } from '../interface/vesr';
import {
	type ApiResult,
	type CapabilitiesOutcome,
	EVIDENCE_CODES,
	evidenceBase,
	findCancellationForUnit,
	getCapabilities,
	type ICancellationRow,
	type IRegisterEvidenceRequest,
	type IReportRequest,
	type IReportResponse,
	type ISignEvidenceRequest,
	isRetryable,
	isTerminalEvidenceRefusal,
	linkIncident,
	registerEvidence,
	reportCancellation,
	reportCancellationBaseline,
	resultMessage,
	signEvidence,
	withdrawCancellation,
} from './cancellation-api';
import { deleteMedia, getMedia } from './cancellation-media';
import {
	getRecord,
	type ICancellationRecord,
	type IDelivery,
	type IEvidenceItem,
	listRecords,
	rescueEvidence,
	updateEvidence,
	updateRecord,
	writeServerRow,
} from './cancellation-store';
import {
	type IRelaySession,
	type IRelayTransport,
	isStoragePaused,
	RELAY_CODES,
	relayTransport,
	runRelayUpload,
	sessionUsable,
	sha256Hex,
} from './media-relay';
import { hasExhaustedAttempts, MAX_ATTEMPTS, nextAttemptAt } from './support-backoff';
import { createIncident } from './vesr-api';

export interface ISyncDeps {
	/** `recheckNegative`: a cached "no evidence" is asked again rather than trusted. */
	getCapabilities: (electionId: number, options?: { recheckNegative?: boolean }) => Promise<CapabilitiesOutcome>;
	report: (electionId: number, body: IReportRequest) => Promise<ApiResult<IReportResponse>>;
	reportBaseline: (electionId: number, body: IReportRequest) => Promise<ApiResult<IReportResponse>>;
	sign: (electionId: number, id: number, body: ISignEvidenceRequest) => Promise<ApiResult<IRelaySession>>;
	/** Chunk PUT / resume / complete for one report's evidence routes. */
	relay: (electionId: number, id: number) => IRelayTransport;
	sha256: (blob: Blob) => Promise<string | undefined>;
	register: (electionId: number, id: number, body: IRegisterEvidenceRequest) => Promise<ApiResult<{ id: number }>>;
	link: (electionId: number, id: number, vesrIncidentId: string) => Promise<ApiResult<ICancellationRow>>;
	withdraw: (electionId: number, id: number) => Promise<ApiResult<ICancellationRow>>;
	find: (electionId: number, puCode: string, mode: 'v2' | 'baseline') => Promise<ApiResult<ICancellationRow | null>>;
	createIncident: (body: ICreateIncidentRequest, idempotencyKey: string) => Promise<IVesrOutcome<IIncident>>;
	getMedia: (id: string) => Promise<Blob | null>;
	deleteMedia: (id: string) => Promise<void>;
	now: () => number;
	random: () => number;
}

export const defaultSyncDeps: ISyncDeps = {
	getCapabilities: (e, options) => getCapabilities(e, options),
	report: reportCancellation,
	reportBaseline: reportCancellationBaseline,
	sign: signEvidence,
	relay: (electionId, id) => relayTransport(evidenceBase(electionId, id)),
	sha256: sha256Hex,
	register: registerEvidence,
	link: linkIncident,
	withdraw: withdrawCancellation,
	find: findCancellationForUnit,
	createIncident,
	getMedia,
	deleteMedia,
	now: () => Date.now(),
	random: Math.random,
};

// ---------------------------------------------------------------------------
// Microcopy (plan: sentence case, plain verbs, no apology)
// ---------------------------------------------------------------------------

export const COPY = {
	notAssigned: 'This unit isn’t assigned to you.',
	notEnabled: 'Reporting cancellations isn’t switched on for this election yet. Your report is kept on this phone.',
	refused: 'The server refused this report.',
	windowClosed: 'Withdraw window closed — ask your party admin to reinstate.',
	alreadyReviewed: 'Your party has already reviewed this report — ask your party admin to reinstate.',
	notActive: 'This report is no longer active.',
	withdrawOffline: 'No signal. Withdraw needs a connection — try again in a moment.',
	withdrawn: 'Withdrawn.',
	evidenceNotAllowed: 'Only the reporter or your party admin can add evidence here.',
	evidenceRefused: 'This file can’t be added.',
	evidenceMediaRejected: 'The server couldn’t accept this file. Attach a different photo or video.',
	evidencePaused:
		'Uploads are paused on the server. Your photos and videos are saved on this phone and will upload automatically.',
} as const;

// ---------------------------------------------------------------------------

const isDue = (d: IDelivery, now: number, force: boolean): boolean => {
	if (d.state === 'held') return force;
	if (d.state !== 'queued' && d.state !== 'sending') return false;
	return force || d.nextAttemptAt <= now;
};

/** One more failed attempt: back off, or hold once the ladder is spent. Never discards. */
const failed = <T extends IDelivery>(d: T, deps: ISyncDeps, error?: string): T => {
	const attempts = d.attempts + 1;
	return hasExhaustedAttempts(attempts)
		? { ...d, attempts, state: 'held', error }
		: { ...d, attempts, state: 'queued', nextAttemptAt: nextAttemptAt(attempts, deps.now(), deps.random), error };
};

/**
 * One more failed attempt for a file. Storage being paused never exhausts the ladder (the file
 * keeps retrying on its own at the ladder's ceiling), and a 429's `Retry-After` is a floor on
 * the delay. `error` is only ever our own copy — never a storage provider's text.
 */
const failedItem = (
	item: IEvidenceItem,
	deps: ISyncDeps,
	{ code, retryAfterMs }: { code?: string; retryAfterMs?: number } = {}
): Partial<IEvidenceItem> => {
	const paused = isStoragePaused(code);
	const attempts = paused ? Math.min(item.attempts + 1, MAX_ATTEMPTS - 1) : item.attempts + 1;
	const error = paused ? COPY.evidencePaused : undefined;
	if (!paused && hasExhaustedAttempts(attempts)) return { attempts, state: 'held', error, code };
	const now = deps.now();
	const at = Math.max(nextAttemptAt(attempts, now, deps.random), retryAfterMs ? now + retryAfterMs : 0);
	return { attempts, state: 'queued', nextAttemptAt: at, error, code };
};

/** Copy for a file the server refused for good. Server text only for an api.core refusal with no storage code. */
const evidenceRefusal = (status: number, code?: string, message?: string): string => {
	if (code === RELAY_CODES.mediaRejected) return COPY.evidenceMediaRejected;
	if (status === 403) return COPY.evidenceNotAllowed;
	if (code && (code.startsWith('STORAGE_') || code.startsWith('UPLOAD_'))) return COPY.evidenceRefused;
	return message || COPY.evidenceRefused;
};

/**
 * A 4xx on one file. Final only for {@link isTerminalEvidenceRefusal}; an uncoded 400 gets one
 * more try; everything else (the report not visible yet, an asset still settling in storage,
 * a permission that lands with the next roll) backs off on the ladder like a 5xx.
 */
const refusedItem = (
	item: IEvidenceItem,
	deps: ISyncDeps,
	{ status, code, message }: { status: number; code?: string; message?: string }
): Partial<IEvidenceItem> => {
	const final = isTerminalEvidenceRefusal(status, code) || (status === 400 && !code && item.badRequestRetried);
	if (final) return { state: 'rejected', code, error: evidenceRefusal(status, code, message) };
	return { ...failedItem(item, deps, { code }), ...(status === 400 && !code ? { badRequestRetried: true } : {}) };
};

const latestItem = (electionId: number, puCode: string, item: IEvidenceItem): IEvidenceItem =>
	getRecord(electionId, puCode)?.evidence.find((e) => e.id === item.id) ?? item;

const seg = (code: string, n: number) => {
	const parts = code.split('-');
	return parts.length >= n ? parts.slice(0, n).join('-') : undefined;
};

/**
 * The Situation Room incident. VESR is not party-scoped — every party's room can read it — so
 * it carries only what INEC itself made public: the unit and the reason. Never the agent's
 * notes, never media.
 */
export const buildNeutralIncident = (record: ICancellationRecord): ICreateIncidentRequest => {
	const code = record.pollingUnitCode;
	const name = record.pollingUnitName ? ` · ${record.pollingUnitName}` : '';
	return {
		title: 'Polling unit cancelled by INEC',
		description: `Polling unit ${code}${name}. Reason: ${reasonLabel(record.reason)}.`,
		category: reasonCategory(record.reason),
		severity: 'High',
		stateCode: seg(code, 1),
		lgaCode: seg(code, 2),
		wardCode: seg(code, 3),
		pollingUnitCode: code,
		latitude: record.coords?.latitude,
		longitude: record.coords?.longitude,
		electionId: String(record.electionId),
	};
};

const buildReportRequest = (r: ICancellationRecord): IReportRequest => ({
	pollingUnitCode: r.pollingUnitCode,
	reason: r.reason,
	notes: r.notes?.trim() || undefined,
	cancelledAt: r.cancelledAt,
	clientReferenceId: r.referenceId,
	latitude: r.coords?.latitude,
	longitude: r.coords?.longitude,
	accuracyMeters: r.coords?.accuracy ?? undefined,
});

const EVIDENCE_ORDER: Record<IEvidenceItem['kind'], number> = { Image: 0, Audio: 1, Video: 2 };

export interface ISyncOptions {
	/** Ignore backoff and retry held steps (a manual Retry or a fresh Send). */
	force?: boolean;
	deps?: ISyncDeps;
	onProgress?: (evidenceId: string, percent: number) => void;
}

const inflight = new Map<string, Promise<ICancellationRecord | null>>();

/** True while a sync for this unit is running in this tab. */
export const isSyncing = (electionId: number, puCode: string) => inflight.has(`${electionId}:${puCode}`);

export const syncCancellation = (
	electionId: number,
	puCode: string,
	options: ISyncOptions = {}
): Promise<ICancellationRecord | null> => {
	const key = `${electionId}:${puCode}`;
	const running = inflight.get(key);
	if (running) return running;
	const run = runSync(electionId, puCode, options).finally(() => inflight.delete(key));
	inflight.set(key, run);
	return run;
};

const runSync = async (
	electionId: number,
	puCode: string,
	{ force = false, deps = defaultSyncDeps, onProgress }: ISyncOptions
) => {
	let record = getRecord(electionId, puCode);
	if (!record || record.withdrawnAt) return record;

	let caps: CapabilitiesOutcome | null = null;
	const capabilities = async () => {
		// A manual run (Send, Retry, "Try again") never takes a cached no for an answer: the agent
		// is usually retrying because someone just turned evidence on. A cached yes still holds.
		if (!caps || caps.mode === 'unknown') caps = await deps.getCapabilities(electionId, { recheckNegative: force });
		return caps;
	};

	// ── 1. core report ──
	if (record.report.state !== 'sent') {
		if (!isDue(record.report, deps.now(), force)) return record;
		const c = await capabilities();
		if (c.mode === 'unknown') {
			return updateRecord(electionId, puCode, (r) => ({ ...r, report: failed(r.report, deps) }));
		}
		if (c.mode === 'v2' && !c.caps.agentReport) {
			return updateRecord(electionId, puCode, (r) => ({ ...r, report: { ...r.report, state: 'held', error: COPY.notEnabled } }));
		}
		record = updateRecord(electionId, puCode, (r) => ({ ...r, report: { ...r.report, state: 'sending' } }));
		if (!record) return null;
		const call = c.mode === 'v2' ? deps.report : deps.reportBaseline;
		const body = buildReportRequest(record);
		let res = await call(electionId, body);
		// 409 = a concurrent write for this unit landed first. Resend once; the server then
		// answers alreadyCancelled / replayed with the row.
		if (res.kind === 'conflict') res = await call(electionId, body);

		const baseline = c.mode === 'baseline';
		record = updateRecord(electionId, puCode, (r) => {
			if (r.withdrawnAt) return r;
			if (res.kind === 'ok') {
				return {
					...r,
					baseline,
					report: { ...r.report, state: 'sent', error: undefined, code: undefined },
					sentAt: r.sentAt ?? new Date(deps.now()).toISOString(),
					row: res.data.row,
					alreadyCancelled: res.data.alreadyCancelled,
				};
			}
			if (res.kind === 'rejected' && res.status !== 401) {
				const notAssigned = res.code === 'NOT_ASSIGNED_TO_POLLING_UNIT';
				return {
					...r,
					report: {
						...r.report,
						state: 'refused',
						code: res.code,
						error: notAssigned ? COPY.notAssigned : res.message || COPY.refused,
					},
				};
			}
			return { ...r, report: failed(r.report, deps, res.kind === 'offline' ? undefined : (res as { message?: string }).message) };
		});
		if (!record) return null;
		if (res.kind === 'ok' && res.data.row) writeServerRow(electionId, puCode, res.data.row);
		if (record.report.state !== 'sent') return record;
	}

	const row = record.row;
	if (!row) return record;

	// ── 2. evidence — each file on its own; one failure never blocks the others ──
	record = await rescueEvidence(record, async (id) => !!(await deps.getMedia(id)));
	const items = [...record.evidence].sort((a, b) => EVIDENCE_ORDER[a.kind] - EVIDENCE_ORDER[b.kind]);
	for (const item of items) {
		// `unsupported` is only "not when we last asked": once the server says v2 + evidence it sends.
		if (item.state === 'unsupported') {
			if (record.baseline) continue;
			const c = await capabilities();
			if (c.mode !== 'v2' || !c.caps.evidence) continue;
		} else {
			const pending = item.state === 'queued' || item.state === 'uploading' || item.state === 'uploaded' || item.state === 'held';
			if (!pending) continue;
			if (item.state === 'held' && !force) continue;
			if (item.state !== 'held' && !force && item.nextAttemptAt > deps.now()) continue;
		}

		const c = await capabilities();
		if (c.mode === 'unknown') {
			updateEvidence(electionId, puCode, item.id, failedItem(item, deps));
			continue;
		}
		if (record.baseline || c.mode === 'baseline' || !c.caps.evidence) {
			updateEvidence(electionId, puCode, item.id, { state: 'unsupported' });
			continue;
		}

		// A held file keeps a storagePublicId only when it was the register step that held it.
		let storagePublicId = item.state === 'uploaded' || item.state === 'held' ? item.storagePublicId : undefined;

		if (!storagePublicId) {
			const blob = await deps.getMedia(item.id);
			if (!blob) {
				updateEvidence(electionId, puCode, item.id, { state: 'missing', error: 'Re-attach this file.' });
				continue;
			}
			const sha256 = item.sha256 ?? (await deps.sha256(blob));
			if (sha256 && !item.sha256) updateEvidence(electionId, puCode, item.id, { sha256 });

			let session = sessionUsable(item.session, deps.now()) ? item.session! : undefined;
			if (!session) {
				const signed = await deps.sign(electionId, row.id, {
					kind: item.kind,
					mime: item.mime,
					bytes: blob.size,
					// The item's local id is a UUID minted at capture — stable across every retry.
					clientUploadId: item.id,
					fileName: item.name,
					sha256,
				});
				if (signed.kind !== 'ok') {
					if (signed.kind === 'rejected' && signed.status !== 401) {
						updateEvidence(electionId, puCode, item.id, {
							...refusedItem(latestItem(electionId, puCode, item), deps, signed),
							session: undefined,
						});
					} else {
						const code = 'code' in signed ? signed.code : undefined;
						updateEvidence(electionId, puCode, item.id, { ...failedItem(item, deps, { code }), session: undefined });
					}
					continue;
				}
				session = signed.data;
				updateEvidence(electionId, puCode, item.id, { session });
				if (session.alreadyStored) storagePublicId = session.storagePublicId;
			}

			if (!storagePublicId) {
				updateEvidence(electionId, puCode, item.id, { state: 'uploading', error: undefined, code: undefined });
				// A photo is one chunk at the server's chunk size; a video may be several.
				const out = await runRelayUpload(session, blob, deps.relay(electionId, row.id), {
					onProgress: (p) => onProgress?.(item.id, p),
				});
				if (out.kind === 'refused') {
					// Another user's session, say: a fresh session may succeed where this one can't.
					updateEvidence(electionId, puCode, item.id, {
						...refusedItem(latestItem(electionId, puCode, item), deps, out),
						session: undefined,
					});
					continue;
				}
				if (out.kind !== 'done') {
					const code = out.kind === 'retry' || out.kind === 'restart' ? out.code : undefined;
					const retryAfterMs = out.kind === 'retry' ? out.retryAfterMs : undefined;
					const latest = latestItem(electionId, puCode, item);
					updateEvidence(electionId, puCode, item.id, {
						...failedItem(latest, deps, { code, retryAfterMs }),
						// A dead session (expired, bad range, hash mismatch) is re-signed next attempt.
						...(out.kind === 'restart' ? { session: undefined } : {}),
					});
					continue;
				}
				storagePublicId = out.data.storagePublicId;
			}
			updateEvidence(electionId, puCode, item.id, { state: 'uploaded', storagePublicId, attempts: 0 });
		}

		const reg = await deps.register(electionId, row.id, {
			kind: item.kind,
			storagePublicId,
			mime: item.mime,
			bytes: item.bytes,
			durationSec: item.durationSec,
			capturedAt: item.capturedAt,
			latitude: record.coords?.latitude,
			longitude: record.coords?.longitude,
		});
		if (reg.kind === 'ok') {
			updateEvidence(electionId, puCode, item.id, { state: 'registered', error: undefined });
			void deps.deleteMedia(item.id);
		} else if (reg.kind === 'rejected' && reg.status !== 401) {
			const patch = refusedItem(latestItem(electionId, puCode, item), deps, reg);
			if (patch.state === 'rejected') updateEvidence(electionId, puCode, item.id, patch);
			else if (reg.code === EVIDENCE_CODES.assetNotStored) {
				// MediaStash never got the whole file: forget the asset and session, upload from scratch.
				updateEvidence(electionId, puCode, item.id, {
					...patch,
					state: patch.state === 'held' ? 'held' : 'queued',
					storagePublicId: undefined,
					session: undefined,
				});
			} else updateEvidence(electionId, puCode, item.id, { ...patch, state: patch.state === 'held' ? 'held' : 'uploaded' });
		} else {
			// Includes 409 STORAGE_PENDING: the asset isn't confirmed yet — back off and register again.
			const code = 'code' in reg ? reg.code : undefined;
			const latest = latestItem(electionId, puCode, item);
			updateEvidence(electionId, puCode, item.id, { ...failedItem(latest, deps, { code }), state: 'uploaded' });
		}
	}

	// ── 3. neutral Situation Room incident → link on the core row ──
	record = getRecord(electionId, puCode);
	if (!record) return null;
	const inc = record.incident;
	const incidentOpen = inc.incidentState === 'pending' || inc.incidentState === 'created' || inc.incidentState === 'held';
	if (record.alreadyCancelled || row.vesrIncidentId) {
		if (inc.incidentState === 'pending') {
			record = updateRecord(electionId, puCode, (r) => ({
				...r,
				incident: { ...r.incident, incidentState: 'skipped', state: 'sent' },
			}));
		}
		return record;
	}
	if (!incidentOpen || !isDue({ ...inc, state: inc.incidentState === 'held' ? 'held' : 'queued' }, deps.now(), force))
		return record;

	let vesrIncidentId = inc.vesrIncidentId;
	if (!vesrIncidentId) {
		const out = await deps.createIncident(buildNeutralIncident(record), record.referenceId);
		if (out.reachable && out.httpStatus >= 200 && out.httpStatus < 300 && out.body?.id) {
			vesrIncidentId = out.body.id;
			const id = vesrIncidentId;
			record = updateRecord(electionId, puCode, (r) => ({
				...r,
				incident: { ...r.incident, incidentState: 'created', vesrIncidentId: id, attempts: 0, nextAttemptAt: 0, state: 'queued' },
			}));
		} else if (
			out.reachable &&
			out.httpStatus >= 400 &&
			out.httpStatus < 500 &&
			out.httpStatus !== 429 &&
			out.httpStatus !== 401
		) {
			return updateRecord(electionId, puCode, (r) => ({
				...r,
				incident: { ...r.incident, incidentState: 'skipped', state: 'refused', error: out.errorMessage },
			}));
		} else {
			return updateRecord(electionId, puCode, (r) => {
				const next = failed(r.incident, deps);
				return { ...r, incident: { ...next, incidentState: next.state === 'held' ? 'held' : r.incident.incidentState } };
			});
		}
	}
	if (!record || !vesrIncidentId) return record;
	if (record.baseline) {
		// v1 has no link route — the incident stands on its own.
		return updateRecord(electionId, puCode, (r) => ({
			...r,
			incident: { ...r.incident, incidentState: 'linked', state: 'sent' },
		}));
	}
	const linked = await deps.link(electionId, row.id, vesrIncidentId);
	// 409 = a different incident is already linked; nothing for this phone to fix.
	if (linked.kind === 'ok' || linked.kind === 'conflict' || linked.kind === 'rejected') {
		if (linked.kind === 'ok') writeServerRow(electionId, puCode, linked.data);
		return updateRecord(electionId, puCode, (r) => ({
			...r,
			row: linked.kind === 'ok' ? linked.data : r.row,
			incident: { ...r.incident, incidentState: 'linked', state: 'sent' },
		}));
	}
	return updateRecord(electionId, puCode, (r) => {
		const next = failed(r.incident, deps);
		return { ...r, incident: { ...next, incidentState: next.state === 'held' ? 'held' : 'created' } };
	});
};

/** App start / `online`: push every record on the device that has something due. */
export const flushDueCancellations = async (deps: ISyncDeps = defaultSyncDeps): Promise<void> => {
	const me = Number(getUserData()?.user?.UserId) || null;
	for (const record of listRecords()) {
		if (record.withdrawnAt) continue;
		// Records belong to whoever filed them; another agent on a shared phone must not send them.
		if (me && record.userId && record.userId !== me) continue;
		await syncCancellation(record.electionId, record.pollingUnitCode, { deps });
	}
};

export type WithdrawOutcome = { ok: true; message: string } | { ok: false; message: string };

/**
 * Withdraw. A report still on the phone is cancelled locally and never sent. A sent one needs
 * the server (the window is the server's, and a withdraw queued offline could land after it
 * closes), so offline is said plainly rather than queued.
 */
export const withdrawReport = async (
	electionId: number,
	puCode: string,
	deps: ISyncDeps = defaultSyncDeps
): Promise<WithdrawOutcome> => {
	const key = `${electionId}:${puCode}`;
	const running = inflight.get(key);
	if (running) await running;

	const record = getRecord(electionId, puCode);
	if (!record) return { ok: false, message: COPY.notActive };

	if (record.report.state !== 'sent') {
		updateRecord(electionId, puCode, (r) => ({
			...r,
			withdrawnAt: new Date(deps.now()).toISOString(),
			report: { ...r.report, state: 'cancelled' },
		}));
		return { ok: true, message: COPY.withdrawn };
	}
	if (!record.row) return { ok: false, message: COPY.notActive };

	const res = await deps.withdraw(electionId, record.row.id);
	if (res.kind === 'ok') {
		writeServerRow(electionId, puCode, res.data);
		updateRecord(electionId, puCode, (r) => ({ ...r, row: res.data, withdrawnAt: new Date(deps.now()).toISOString() }));
		return { ok: true, message: COPY.withdrawn };
	}
	if (res.kind === 'conflict') {
		if (res.code === 'WITHDRAW_WINDOW_CLOSED') return { ok: false, message: COPY.windowClosed };
		if (res.code === 'ALREADY_REVIEWED') return { ok: false, message: COPY.alreadyReviewed };
		return { ok: false, message: COPY.notActive };
	}
	if (isRetryable(res)) return { ok: false, message: COPY.withdrawOffline };
	return { ok: false, message: resultMessage(res) || COPY.notActive };
};
